import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { ChevronDown, ThumbsDown, Lightbulb, AlertTriangle, XCircle, Info, CheckCircle, Timer } from "lucide-react";
import LlmMetricCard from "../metrics/LlmMetricCard";

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const COMPUTING_PHRASES = [
  "Crunching audio waveforms…",
  "Untangling speaker voices…",
  "Measuring response gaps…",
  "Teaching robots to listen…",
  "Counting interruptions…",
  "Chasing milliseconds…",
  "Listening very carefully…",
  "Separating signal from noise…",
  "Timing the silences…",
  "Reconstructing the conversation…",
  "Decoding speaker channels…",
  "Running VAD pipeline…",
  "Analyzing turn dynamics…",
  "Mapping the audio timeline…",
  "Detecting acoustic patterns…",
  "Almost there…",
  "Still thinking…",
];

export const LLM_COMPUTING_PHRASES = [
  "Consulting the oracle…",
  "Reading between the lines…",
  "Evaluating against criteria…",
  "Asking the LLM nicely…",
  "Cross-referencing the rubric…",
  "Weighing every word…",
  "Letting the model think…",
  "Reasoning through the transcript…",
  "Checking for compliance…",
  "Running semantic analysis…",
  "Almost done judging…",
  "The LLM is deliberating…",
];

export const ComputingBadge = ({ className = "" }) => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % COMPUTING_PHRASES.length),
      1800,
    );
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className={`text-xs text-indigo-500 font-medium transition-all ${className}`}
    >
      {COMPUTING_PHRASES[idx]}
    </span>
  );
};

export const LlmComputingBadge = ({ className = "" }) => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % LLM_COMPUTING_PHRASES.length),
      1800,
    );
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className={`text-xs text-violet-500 font-medium transition-all ${className}`}
    >
      {LLM_COMPUTING_PHRASES[idx]}
    </span>
  );
};

export const formatTimestamp = (ms) => {
  if (ms == null) return "—";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
};

export const formatDuration = (sec) => {
  if (!sec) return "—";
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

/**
 * Builds a metric lookup map and helper functions from an array of standard_metrics.
 */
export const useMetricsMap = (standardMetrics = []) => {
  const metricsByKey = {};
  standardMetrics.forEach((m) => {
    metricsByKey[m.metric_key] = m;
  });

  // Did the backend send this metric at all? A metric the agent has DISABLED is
  // absent from the payload entirely — and `isPending` below reads absence as
  // "pending", so an unguarded card renders a "Chasing milliseconds…" spinner
  // forever for a metric that is never going to be computed. Gate each card on
  // `has` so the panel shows exactly what the backend says it evaluates.
  const has = (key) => Boolean(metricsByKey[key]);

  const isPending = (key) => {
    const m = metricsByKey[key];
    return !m || m.status === "pending" || m.status === "processing";
  };
  const isSkipped = (key) => metricsByKey[key]?.result_data?.skipped === true;
  const isFailed = (key) => metricsByKey[key]?.status === "failed";
  const getResultData = (key) => metricsByKey[key]?.result_data || null;

  return { metricsByKey, has, isPending, isSkipped, isFailed, getResultData };
};

// ─── Latency chart ────────────────────────────────────────────────────────────

export const LatencyChart = ({ resultData, onTimestampClick, isPending }) => {
  const [showTurns, setShowTurns] = useState(false);
  if (isPending || (!resultData && !isPending)) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <svg
          className="animate-spin h-5 w-5 text-indigo-400"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
        <ComputingBadge className="text-sm" />
      </div>
    );
  }

  if (resultData?.skipped) {
    const isAwaiting = resultData.reason?.includes("Awaiting");
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        {isAwaiting ? (
          <>
            <svg
              className="animate-spin h-5 w-5 text-indigo-400"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            <ComputingBadge className="text-sm" />
          </>
        ) : (
          <>
            <span className="text-amber-600 text-sm font-medium">
              No recording provided
            </span>
            <span className="text-xs text-muted-foreground">
              Submit a recording_url to enable latency analysis
            </span>
          </>
        )}
      </div>
    );
  }

  const turns = resultData?.turns || [];

  if (turns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2 text-center px-4">
        <p className="text-sm text-muted-foreground">No turn-level latency detected</p>
        <p className="text-xs text-gray-300 max-w-xs">
          Per-speaker latency requires separate audio channels. Web call
          recordings mix both speakers, so turn transitions cannot be isolated.
        </p>
      </div>
    );
  }

  const values = turns.map((t) => t.latency_ms);
  const pct = (p) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.ceil((p / 100) * sorted.length) - 1] || 0;
  };

  const chartData = turns.map((t, i) => ({
    turn: `T${i + 1}`,
    latency: Math.round(t.latency_ms),
    timestamp_ms: t.timestamp_ms,
    timestamp_fmt: t.timestamp_fmt,
  }));

  const PLINES = [
    { value: pct(25), color: "#22c55e", label: `P25 (${Math.round(pct(25))}ms)` },
    { value: pct(50), color: "#3b82f6", label: `P50 (${Math.round(pct(50))}ms)` },
    { value: pct(75), color: "#a855f7", label: `P75 (${Math.round(pct(75))}ms)` },
    { value: pct(90), color: "#f97316", label: `P90 (${Math.round(pct(90))}ms)` },
    { value: pct(95), color: "#ef4444", label: `P95 (${Math.round(pct(95))}ms)` },
    { value: pct(99), color: "#7f1d1d", label: `P99 (${Math.round(pct(99))}ms)` },
  ];

  return (
    <div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="turn" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="ms" />
          <Tooltip formatter={(v) => [`${v}ms`, "Latency"]} />
          {PLINES.map((pl) => (
            <ReferenceLine
              key={pl.label}
              y={pl.value}
              stroke={pl.color}
              strokeDasharray="4 4"
              strokeOpacity={0.7}
            />
          ))}
          <Line
            type="monotone"
            dataKey="latency"
            stroke="#6366f1"
            strokeWidth={2}
            dot={{
              r: 4,
              fill: "#6366f1",
              cursor: onTimestampClick ? "pointer" : "default",
            }}
            activeDot={{
              r: 6,
              cursor: "pointer",
              onClick: (_, payload) => {
                if (onTimestampClick && payload?.payload?.timestamp_ms != null)
                  onTimestampClick(payload.payload.timestamp_ms);
              },
            }}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-3 mt-2 justify-center">
        {PLINES.map((pl) => (
          <span
            key={pl.label}
            className="flex items-center gap-1 text-xs text-muted-foreground"
          >
            <span
              className="inline-block w-5 border-t-2 border-dashed"
              style={{ borderColor: pl.color }}
            />
            {pl.label}
          </span>
        ))}
      </div>
      {/* Collapsible turn list */}
      <div className="mt-3">
        <button
          onClick={() => setShowTurns((v) => !v)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-medium"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTurns ? "" : "-rotate-90"}`} />
          Turn-by-turn latency ({chartData.length} turns)
        </button>
        {showTurns && (
          <div className="mt-2 flex flex-wrap gap-2">
            {chartData.map((t) => (
              <button
                key={t.turn}
                onClick={() => onTimestampClick && onTimestampClick(t.timestamp_ms)}
                className="flex items-center gap-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-mono px-2 py-1 rounded transition-colors"
              >
                {t.turn} · {t.timestamp_fmt} · {t.latency}ms
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Expandable interruption/event metric card ────────────────────────────────

export const EventMetricCard = ({
  title,
  count,
  events,
  timestampKey = "timestamp_fmt",
  descriptionLine,
  onTimestampClick,
  isPending,
  isSkipped,
  isFailed,
  skipReason,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isComputing = isSkipped && skipReason?.includes("Awaiting");
  const canExpand = !isPending && !isSkipped && !isFailed;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => canExpand && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{title}</span>
          {canExpand && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {(isPending || isComputing) && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">
              Failed
            </span>
          )}
          {isSkipped && !isComputing && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded">
              No recording
            </span>
          )}
          {canExpand && (
            <>
              <span className="text-sm font-bold text-foreground">{count}</span>
              <ThumbsDown className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </div>
      </button>

      {isFailed && (
        <div className="border-t border-red-100 px-5 py-3 bg-red-50 text-xs text-red-600">
          Analysis failed — re-ingest the call to retry.
        </div>
      )}

      {isSkipped && !isComputing && (
        <div className="border-t border-amber-100 px-5 py-3 bg-amber-50 text-xs text-amber-700">
          Requires audio. Submit a{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">
            recording_url
          </code>{" "}
          when ingesting this call to enable this metric.
        </div>
      )}

      {expanded && events && events.length > 0 && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          {descriptionLine && (
            <p className="text-sm text-muted-foreground mb-3">{descriptionLine}</p>
          )}
          <div className="space-y-1">
            {events.map((ev, i) => {
              const ts =
                ev[timestampKey] || ev.timestamp_fmt || ev.start_fmt || "";
              return (
                <div key={i} className="flex items-center gap-1 text-sm">
                  <span className="text-muted-foreground">-</span>
                  <button
                    className="text-blue-500 hover:text-blue-700 font-mono"
                    onClick={() =>
                      onTimestampClick &&
                      onTimestampClick(ev.timestamp_ms ?? ev.start_ms ?? 0)
                    }
                  >
                    {ts}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {expanded && (!events || events.length === 0) && (
        <div className="border-t border-border px-5 py-4 text-sm text-muted-foreground bg-muted/50">
          No events detected.
        </div>
      )}
    </div>
  );
};

// ─── Silence card ─────────────────────────────────────────────────────────────

export const SilenceCard = ({
  resultData,
  isPending,
  isSkipped,
  isFailed,
  onTimestampClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const silences = resultData?.notable_silences || (resultData?.silences || []).filter((s) => s.duration_ms >= 3000);
  const count = resultData?.notable_count ?? silences.length;
  const totalSec = resultData?.notable_total_ms ?? silences.reduce((sum, s) => sum + s.duration_ms, 0);
  const totalSecFmt = Math.round(totalSec / 1000);
  const isComputing = isSkipped && resultData?.reason?.includes("Awaiting");

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => !isPending && !isSkipped && !isFailed && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Silence Detection
          </span>
          {!isPending && !isSkipped && !isFailed && (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {(isPending || isComputing) && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">
              Failed
            </span>
          )}
          {isSkipped && !isComputing && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded">
              No recording
            </span>
          )}
          {!isPending && !isSkipped && !isFailed && (
            <span className="text-sm font-bold text-foreground">{count}</span>
          )}
        </div>
      </button>
      {isFailed && (
        <div className="border-t border-red-100 px-5 py-3 bg-red-50 text-xs text-red-600">
          Analysis failed — re-ingest the call to retry.
        </div>
      )}

      {isSkipped && !isComputing && (
        <div className="border-t border-amber-100 px-5 py-3 bg-amber-50 text-xs text-amber-700">
          Requires audio. Submit a{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">
            recording_url
          </code>{" "}
          when ingesting this call to enable this metric.
        </div>
      )}
      {expanded && !isPending && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          {totalSecFmt > 0 && (
            <p className="text-sm text-muted-foreground mb-3">
              {count} silence{count !== 1 ? "s" : ""} over 3s — total{" "}
              <span className="font-medium">{totalSecFmt}s</span>
            </p>
          )}
          {silences.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No significant silences detected.
            </p>
          ) : (
            <div className="space-y-1">
              {silences.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">-</span>
                  <button
                    className="text-blue-500 hover:text-blue-700 font-mono"
                    onClick={() =>
                      onTimestampClick && onTimestampClick(s.start_ms)
                    }
                  >
                    {s.start_fmt}
                  </button>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-mono text-muted-foreground">{s.end_fmt}</span>
                  <span className="text-xs text-muted-foreground">
                    ({Math.round(s.duration_ms / 1000)}s)
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Voice change card ────────────────────────────────────────────────────────

export const VoiceChangeCard = ({ resultData, isPending, isFailed, onTimestampClick }) => {
  const [expanded, setExpanded] = useState(false);
  const data = resultData || {};
  const events = data.events || [];
  const assessment = data.assessment || "—";
  const assessmentColor =
    assessment === "stable"
      ? "text-green-700 bg-green-50"
      : assessment === "minor_changes"
        ? "text-amber-700 bg-amber-50"
        : "text-red-700 bg-red-50";

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => !isPending && !isFailed && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Voice Change Detection
          </span>
          {!isPending && !isFailed && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {isPending && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">
              Failed
            </span>
          )}
          {!isPending && !isFailed && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${assessmentColor}`}
            >
              {assessment.replace("_", " ")}
            </span>
          )}
        </div>
      </button>
      {isFailed && (
        <div className="border-t border-red-100 px-5 py-3 bg-red-50 text-xs text-red-600">
          Analysis failed — re-ingest the call to retry.
        </div>
      )}

      {expanded && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No voice changes detected.</p>
          ) : (
            <div className="space-y-1">
              {events.map((ev, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <button
                    className="text-blue-500 hover:text-blue-700 font-mono"
                    onClick={() =>
                      onTimestampClick && onTimestampClick(ev.at * 1000)
                    }
                  >
                    {ev.timestamp_fmt}
                  </button>
                  <span
                    className={`text-xs font-semibold ${ev.severity === "high" ? "text-red-600" : "text-amber-600"}`}
                  >
                    {ev.severity}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    sim {ev.similarity_to_baseline?.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Voice tone & clarity card ────────────────────────────────────────────────

export const VoiceToneClarityCard = ({ resultData, isPending, isFailed }) => {
  const data = resultData || {};
  const overall = data.overall_score;
  const clarity = data.clarity_score;
  const tone = data.tone_score;

  const scoreColor = (s) =>
    s >= 4 ? "bg-green-500" : s >= 3 ? "bg-amber-400" : "bg-red-400";
  const scoreTextColor = (s) =>
    s >= 4 ? "text-green-700" : s >= 3 ? "text-amber-700" : "text-red-700";

  const ScoreBar = ({ label, score }) => (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-14">{label}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${scoreColor(score)}`}
          style={{ width: `${(score / 5) * 100}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-8 ${scoreTextColor(score)}`}>
        {score}/5
      </span>
    </div>
  );

  return (
    <div className="border border-border rounded-xl p-4 bg-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-foreground">
          Voice Tone & Clarity
        </span>
        {!isPending && overall != null && (
          <span className={`text-sm font-bold ${scoreTextColor(overall)}`}>
            {overall}/5
          </span>
        )}
        {isPending && <ComputingBadge />}
      </div>
      {isFailed ? (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-1">
          Analysis failed — re-ingest the call to retry.
        </div>
      ) : !isPending && overall != null ? (
        <div className="space-y-2">
          <ScoreBar label="Clarity" score={clarity} />
          <ScoreBar label="Tone" score={tone} />
        </div>
      ) : !isPending ? (
        <p className="text-sm text-muted-foreground">No data available.</p>
      ) : null}
    </div>
  );
};

// ─── WPM card ─────────────────────────────────────────────────────────────────

export const WpmCard = ({ resultData, isPending, isFailed }) => {
  const data = resultData || {};
  const wpm = data.avg_wpm;
  const assessment = data.assessment;
  const assessmentColor =
    assessment === "natural"
      ? "text-green-700"
      : assessment === "slow"
        ? "text-amber-700"
        : assessment === "fast"
          ? "text-blue-700"
          : "text-red-700";

  return (
    <div className="border border-border rounded-xl p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-foreground">
          Word Per Minute (AI)
        </span>
        {isPending && <ComputingBadge />}
      </div>
      {isFailed ? (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          Analysis failed — re-ingest the call to retry.
        </div>
      ) : !isPending && wpm != null ? (
        <div className="flex items-end gap-4">
          <div>
            <span className={`text-3xl font-bold ${assessmentColor}`}>
              {Math.round(wpm)}
            </span>
            <span className="text-sm text-muted-foreground ml-1">wpm</span>
          </div>
          <div className="pb-1">
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${assessmentColor} bg-opacity-10`}
            >
              {assessment?.replace("_", " ")}
            </span>
            {data.total_words != null && (
              <p className="text-xs text-muted-foreground mt-1">
                {data.total_words} words · estimated from audio
              </p>
            )}
          </div>
        </div>
      ) : !isPending && !isFailed ? (
        <p className="text-sm text-muted-foreground">No data available.</p>
      ) : null}
    </div>
  );
};

// ─── System LLM Metrics section ──────────────────────────────────────────────

export const SystemLlmMetricsSection = ({ metrics = [], onSeek, onMarkIncorrect, onRerunMetric }) => {
  if (metrics.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-4">
        System LLM Metrics
      </h3>
      <div className="flex flex-col gap-3">
        {metrics.map((m) => (
          <LlmMetricCard
            key={m.metric_key}
            metricId={m.metric_id}
            metricName={m.metric_name}
            status={m.status}
            resultType={m.result_type || "enum"}
            value={m.result_data?.value ?? m.value}
            metricResult={m.result_data?.metric_result || m.result_data?.structured_output || m.metric_result}
            explanation={m.result_data?.explanation || m.explanation}
            transcriptTiming={m.result_data?.transcript_timing || m.transcript_timing}
            errorMessage={m.error_message}
            computedAt={m.computed_at}
            onSeek={onSeek}
            onMarkIncorrect={onMarkIncorrect ? () => onMarkIncorrect(m) : undefined}
            onRerunMetric={onRerunMetric}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Custom LLM Metrics section ──────────────────────────────────────────────

export const CustomLlmMetricsSection = ({
  metrics = [],
  onSeek,
  onMarkIncorrect,
  onRerunMetric,
}) => {
  if (metrics.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-4">
        Custom LLM Metrics
      </h3>
      <div className="flex flex-col gap-3">
        {metrics.map((m) => (
          <LlmMetricCard
            key={m.metric_id}
            metricId={m.metric_id}
            metricName={m.metric_name}
            status={m.status}
            resultType={m.result_type || "boolean"}
            value={m.result_data?.value ?? m.value}
            metricResult={m.result_data?.structured_output || m.result_data?.metric_result || m.structured_output || m.metric_result}
            explanation={m.result_data?.explanation || m.explanation}
            transcriptTiming={m.result_data?.transcript_timing || m.transcript_timing}
            scope={m.scope}
            isCritical={m.is_critical}
            errorMessage={m.error_message}
            computedAt={m.computed_at}
            onSeek={onSeek}
            onMarkIncorrect={
              m.status === "computed" && onMarkIncorrect
                ? () => onMarkIncorrect(m)
                : undefined
            }
            onRerunMetric={onRerunMetric}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Call Insights Panel ─────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  major: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50", border: "border-red-200" },
  minor: { icon: Info, color: "text-yellow-500", bg: "bg-yellow-50", border: "border-yellow-200" },
  info: { icon: Lightbulb, color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" },
};

const ASSESSMENT_CONFIG = {
  good: { label: "Good", color: "bg-green-100 text-green-700" },
  needs_improvement: { label: "Needs Improvement", color: "bg-yellow-100 text-yellow-700" },
  poor: { label: "Poor", color: "bg-red-100 text-red-700" },
};

export const CallInsightsPanel = ({ insights }) => {
  const [expanded, setExpanded] = useState(true);

  if (!insights || !insights.observations || insights.observations.length === 0) return null;

  const assessment = ASSESSMENT_CONFIG[insights.overall_assessment] || ASSESSMENT_CONFIG.good;

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-2">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-semibold text-foreground">Call Insights</h3>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${assessment.color}`}>
            {assessment.label}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {insights.summary && (
            <p className="text-sm text-muted-foreground mb-3">{insights.summary}</p>
          )}
          {insights.observations.map((obs, idx) => {
            const sevCfg = SEVERITY_CONFIG[obs.severity] || SEVERITY_CONFIG.info;
            const isPositive = obs.category === "positive";
            const Icon = isPositive ? CheckCircle : sevCfg.icon;
            const iconColor = isPositive ? "text-green-500" : sevCfg.color;
            const bgColor = isPositive ? "bg-green-50" : sevCfg.bg;
            const borderColor = isPositive ? "border-green-200" : sevCfg.border;

            return (
              <div key={idx} className={`${bgColor} border ${borderColor} rounded-lg p-3`}>
                <div className="flex items-start space-x-2">
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{obs.observation}</p>
                    {obs.transcript_excerpt && (
                      <p className="mt-1 text-xs text-muted-foreground italic">"{obs.transcript_excerpt}"</p>
                    )}
                    {obs.suggestion && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        <span className="font-medium">Suggestion:</span> {obs.suggestion}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground uppercase flex-shrink-0">
                    {obs.category?.replace("_", " ")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Standard Metrics Panel (all audio metrics together) ─────────────────────

// ─── Stop Time After User Interruption card ───────────────────────────────────

const StopTimeCard = ({ resultData, isPending, isSkipped, isFailed, onTimestampClick }) => {
  const [expanded, setExpanded] = useState(false);
  const events = resultData?.events || [];
  const count = resultData?.count ?? 0;
  const avgMs = resultData?.avg_ms;
  const canExpand = !isPending && !isSkipped && !isFailed;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => canExpand && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">AI Reaction Time</span>
          {canExpand && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {isPending && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">
              Failed
            </span>
          )}
          {isSkipped && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded">
              No recording
            </span>
          )}
          {canExpand && (
            <>
              {avgMs != null && count > 0 && (
                <span className="text-xs text-muted-foreground">
                  avg <span className="font-semibold text-foreground">{avgMs}ms</span>
                </span>
              )}
              <span className="text-sm font-bold text-foreground">{count}</span>
              <Timer className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </div>
      </button>

      {isFailed && (
        <div className="border-t border-red-100 px-5 py-3 bg-red-50 text-xs text-red-600">
          Analysis failed — re-ingest the call to retry.
        </div>
      )}

      {isSkipped && (
        <div className="border-t border-amber-100 px-5 py-3 bg-amber-50 text-xs text-amber-700">
          Requires audio. Submit a{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">recording_url</code>{" "}
          when ingesting this call to enable this metric.
        </div>
      )}

      {expanded && events.length > 0 && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          <p className="text-sm text-muted-foreground mb-3">
            How long the AI took to respond after the user stopped speaking:
          </p>
          <div className="space-y-1.5">
            {events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <button
                  className="text-blue-500 hover:text-blue-700 font-mono"
                  onClick={() => onTimestampClick?.(ev.at_ms || 0)}
                >
                  {ev.timestamp_fmt}
                </button>
                <span className="text-muted-foreground">→</span>
                <span className="font-semibold text-foreground">{ev.reaction_ms}ms</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {expanded && events.length === 0 && (
        <div className="border-t border-border px-5 py-3 bg-muted/50 text-xs text-muted-foreground">
          No events recorded.
        </div>
      )}
    </div>
  );
};

// ─── False Interruption Detection card ────────────────────────────────────────

const FalseInterruptionCard = ({ resultData, isPending, isSkipped, isFailed, onTimestampClick }) => {
  const [expanded, setExpanded] = useState(false);
  const events = resultData?.events || [];
  const count = resultData?.count ?? 0;
  const rate = resultData?.false_interruption_rate;
  const canExpand = !isPending && !isSkipped && !isFailed;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => canExpand && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">False Interruption Detection</span>
          {canExpand && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {isPending && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">Failed</span>
          )}
          {canExpand && (
            <>
              {rate != null && count > 0 && (
                <span className="text-xs text-muted-foreground">
                  rate <span className="font-semibold text-amber-600">{(rate * 100).toFixed(0)}%</span>
                </span>
              )}
              <span className="text-sm font-bold text-foreground">{count}</span>
              <AlertTriangle className="h-4 w-4 text-amber-400" />
            </>
          )}
        </div>
      </button>

      {expanded && events.length > 0 && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          <p className="text-sm text-muted-foreground mb-3">
            Bot falsely reacted to noise — stopped and restarted:
          </p>
          <div className="space-y-1.5">
            {events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <button
                  className="text-blue-500 hover:text-blue-700 font-mono"
                  onClick={() => onTimestampClick?.(ev.at_ms || 0)}
                >
                  {ev.timestamp_fmt}
                </button>
                <span className="text-muted-foreground">gap</span>
                <span className="font-semibold text-foreground">{ev.gap_ms}ms</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  ev.user_energy_ratio < 0.15
                    ? "bg-red-50 text-red-600"
                    : "bg-amber-50 text-amber-600"
                }`}>
                  energy {(ev.user_energy_ratio * 100).toFixed(0)}%
                </span>
                {ev.text_signals?.apology_detected && (
                  <span className="text-xs bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded">apology</span>
                )}
                {ev.text_signals?.repetition_detected && (
                  <span className="text-xs bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded">repeated</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {expanded && events.length === 0 && (
        <div className="border-t border-border px-5 py-3 bg-muted/50 text-xs text-muted-foreground">
          No false interruptions detected — bot only reacted to real user speech.
        </div>
      )}
    </div>
  );
};

// ─── Interruption Non-Adherence card ──────────────────────────────────────────

const NonAdherenceCard = ({ resultData, isPending, isSkipped, isFailed, onTimestampClick }) => {
  const [expanded, setExpanded] = useState(false);
  const events = resultData?.events || [];
  const count = resultData?.count ?? 0;
  const adherenceRate = resultData?.adherence_rate;
  const avgContinued = resultData?.avg_continued_ms;
  const canExpand = !isPending && !isSkipped && !isFailed;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => canExpand && setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">Interruption Non-Adherence</span>
          {canExpand && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {isPending && <ComputingBadge />}
          {isFailed && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded">Failed</span>
          )}
          {canExpand && (
            <>
              {adherenceRate != null && (
                <span className="text-xs text-muted-foreground">
                  adherence <span className={`font-semibold ${adherenceRate >= 0.8 ? "text-emerald-600" : adherenceRate >= 0.5 ? "text-amber-600" : "text-red-600"}`}>
                    {(adherenceRate * 100).toFixed(0)}%
                  </span>
                </span>
              )}
              {avgContinued != null && count > 0 && (
                <span className="text-xs text-muted-foreground">
                  avg <span className="font-semibold text-foreground">{avgContinued}ms</span>
                </span>
              )}
              <span className="text-sm font-bold text-foreground">{count}</span>
              <XCircle className="h-4 w-4 text-red-400" />
            </>
          )}
        </div>
      </button>

      {expanded && events.length > 0 && (
        <div className="border-t border-border px-5 py-4 bg-muted/50">
          <p className="text-sm text-muted-foreground mb-3">
            User interrupted but bot kept talking beyond {resultData?.threshold_ms || 1500}ms:
          </p>
          <div className="space-y-1.5">
            {events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <button
                  className="text-blue-500 hover:text-blue-700 font-mono"
                  onClick={() => onTimestampClick?.(ev.at_ms || 0)}
                >
                  {ev.timestamp_fmt}
                </button>
                <span className="text-muted-foreground">AI kept talking</span>
                <span className="font-semibold text-foreground">{ev.continued_ms}ms</span>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  ev.severity === "severe"
                    ? "bg-red-50 text-red-600"
                    : "bg-amber-50 text-amber-600"
                }`}>
                  {ev.severity}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {expanded && events.length === 0 && (
        <div className="border-t border-border px-5 py-3 bg-muted/50 text-xs text-muted-foreground">
          Bot adhered to all user interruptions within the threshold.
        </div>
      )}
    </div>
  );
};

// Deterministic pass/fail outcome metric (call_connected / no_response).
// resultData carries { value: "pass"|"fail", explanation } for a scored call, or
// { skipped: true, reason } when N/A (e.g. no_response on a call that never connected).
const OutcomeMetricCard = ({
  title,
  resultData,
  isPending,
  isSkipped,
  isFailed,
  goodLabel,
  badLabel,
}) => {
  const value = (resultData?.value || "").toString().toLowerCase();
  const passed = value === "pass" || value === "passed" || value === "true";
  const skipped =
    value === "not_applicable" ||
    value === "not applicable" ||
    value === "n/a" ||
    isSkipped ||
    resultData?.skipped === true;

  let badge;
  if (isPending) badge = { text: "Pending…", cls: "bg-muted text-muted-foreground" };
  else if (isFailed) badge = { text: "Analysis failed", cls: "bg-red-100 text-red-700" };
  else if (skipped) badge = { text: "N/A", cls: "bg-muted text-muted-foreground" };
  else if (passed) badge = { text: goodLabel, cls: "bg-green-100 text-green-700" };
  else badge = { text: badLabel, cls: "bg-red-100 text-red-700" };

  const detail = !isPending && (resultData?.explanation || resultData?.reason);

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
          {badge.text}
        </span>
      </div>
      {detail && <p className="text-xs text-muted-foreground mt-2">{detail}</p>}
    </div>
  );
};

export const StandardMetricsPanel = ({
  standardMetrics = [],
  analysisFailed = false,
  onTimestampClick,
  onMarkIncorrect,
}) => {
  const { metricsByKey, has, isPending, isSkipped, getResultData } = useMetricsMap(standardMetrics);

  const callConnectedData = getResultData("call_connected");
  const noResponseData = getResultData("no_response");
  const latencyData = getResultData("latency_ms");
  const userIntData = getResultData("user_interruptions");
  const aiIntData = getResultData("ai_interruptions");
  const stopTimeData = getResultData("stop_time_after_interruption");
  const falseIntData = getResultData("false_interruption_detection");
  const nonAdherenceData = getResultData("interruption_non_adherence");
  const silenceData = getResultData("silence_detection");
  const voiceChangeData = getResultData("voice_change_detection");
  const voiceToneData = getResultData("voice_tone_clarity");
  const wpmData = getResultData("word_per_minute");
  const avgLatency = latencyData?.avg_ms;
  const p95Latency = latencyData?.p95_ms;

  if (standardMetrics.length === 0) return null;

  return (
    <>
      {/* Deterministic call-outcome metrics (off by default) */}
      {has("call_connected") && (
        <OutcomeMetricCard
          title="Call Connected"
          resultData={callConnectedData}
          isPending={isPending("call_connected")}
          isSkipped={isSkipped("call_connected")}
          isFailed={analysisFailed && !metricsByKey["call_connected"]}
          goodLabel="Connected"
          badLabel="Not connected"
        />
      )}

      {has("no_response") && (
        <OutcomeMetricCard
          title="No Response"
          resultData={noResponseData}
          isPending={isPending("no_response")}
          isSkipped={isSkipped("no_response")}
          isFailed={analysisFailed && !metricsByKey["no_response"]}
          goodLabel="Responded"
          badLabel="No response"
        />
      )}

      {/* Latency chart */}
      {has("latency_ms") && (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">
            Analysis
          </h3>
          {!isPending("latency_ms") && !analysisFailed && avgLatency != null && (
            <span className="text-xs text-muted-foreground">
              Avg:{" "}
              <span className="font-semibold text-foreground">
                {avgLatency}ms
              </span>{" "}
              · P95:{" "}
              <span className="font-semibold text-foreground">
                {p95Latency}ms
              </span>
            </span>
          )}
        </div>
        {analysisFailed ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2">
            <svg className="h-8 w-8 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="text-sm text-red-500 font-medium">Analysis failed</p>
            <p className="text-xs text-muted-foreground">Re-ingest the call to retry</p>
          </div>
        ) : (
          <LatencyChart
            resultData={latencyData}
            onTimestampClick={onTimestampClick}
            isPending={isPending("latency_ms")}
          />
        )}
      </div>
      )}

      {/* Interruption + silence expandable cards */}
      {has("user_interruptions") && (
        <EventMetricCard
          title="User interrupting AI"
          count={userIntData?.count ?? 0}
          events={userIntData?.events}
          isPending={isPending("user_interruptions")}
          isSkipped={isSkipped("user_interruptions")}
          isFailed={analysisFailed && !metricsByKey["user_interruptions"]}
          skipReason={userIntData?.reason}
          descriptionLine="User interrupted the AI agent at timestamps:"
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("ai_interruptions") && (
        <EventMetricCard
          title="AI interrupting user"
          count={aiIntData?.count ?? 0}
          events={aiIntData?.events}
          isPending={isPending("ai_interruptions")}
          isSkipped={isSkipped("ai_interruptions")}
          isFailed={analysisFailed && !metricsByKey["ai_interruptions"]}
          skipReason={aiIntData?.reason}
          descriptionLine="AI agent interrupted the user at timestamps:"
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("stop_time_after_interruption") && (
        <StopTimeCard
          resultData={stopTimeData}
          isPending={isPending("stop_time_after_interruption")}
          isSkipped={isSkipped("stop_time_after_interruption")}
          isFailed={analysisFailed && !metricsByKey["stop_time_after_interruption"]}
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("false_interruption_detection") && (
        <FalseInterruptionCard
          resultData={falseIntData}
          isPending={isPending("false_interruption_detection")}
          isSkipped={isSkipped("false_interruption_detection")}
          isFailed={analysisFailed && !metricsByKey["false_interruption_detection"]}
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("interruption_non_adherence") && (
        <NonAdherenceCard
          resultData={nonAdherenceData}
          isPending={isPending("interruption_non_adherence")}
          isSkipped={isSkipped("interruption_non_adherence")}
          isFailed={analysisFailed && !metricsByKey["interruption_non_adherence"]}
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("silence_detection") && (
        <SilenceCard
          resultData={silenceData}
          isPending={isPending("silence_detection")}
          isSkipped={isSkipped("silence_detection")}
          isFailed={analysisFailed && !metricsByKey["silence_detection"]}
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("voice_change_detection") && (
        <VoiceChangeCard
          resultData={voiceChangeData}
          isPending={isPending("voice_change_detection")}
          isFailed={analysisFailed && !metricsByKey["voice_change_detection"]}
          onTimestampClick={onTimestampClick}
        />
      )}

      {has("voice_tone_clarity") && (
        <VoiceToneClarityCard
          resultData={voiceToneData}
          isPending={isPending("voice_tone_clarity")}
          isFailed={analysisFailed && !metricsByKey["voice_tone_clarity"]}
        />
      )}

      {has("word_per_minute") && (
        <WpmCard
          resultData={wpmData}
          isPending={isPending("word_per_minute")}
          isFailed={analysisFailed && !metricsByKey["word_per_minute"]}
        />
      )}
    </>
  );
};

// ─── Transcript panel (rich — supports role-based, utterance-based, legacy) ──

// The system prompt is not a conversation turn — Vapi/Retell include it in their
// messages array, so drop any "system" role from the displayed transcript.
const isSystemRole = (role) => (role || "").trim().toLowerCase() === "system";

// Map vendor role names (Vapi/Retell use lowercase "assistant"/"user", "bot",
// etc.) onto the canonical labels the rest of the UI uses, so they render and
// style the same as non-vapi/non-retell calls. Unknown/function-call labels pass
// through unchanged.
const normalizeRole = (role) => {
  const r = (role || "").trim().toLowerCase();
  if (["assistant", "ai assistant", "bot", "agent", "ai"].includes(r)) return "AI Assistant";
  if (["user", "customer", "human", "caller"].includes(r)) return "User";
  return role || "Unknown";
};

export const parseRoleTranscript = (text) => {
  // Array format — stored by the public API (supports Function Call turns)
  if (Array.isArray(text)) {
    return text
      .filter((turn) => !isSystemRole(turn.role))
      .map((turn) => ({
        label: normalizeRole(turn.role),
        text: turn.content || "",
        fnData: turn.data || null,
        start: turn.start_time != null ? turn.start_time * 1000 : null,
        end:   turn.end_time   != null ? turn.end_time   * 1000 : null,
        isFunctionCall: turn.role === "Function Call" || turn.role === "Function Call Result",
        isFnResult: turn.role === "Function Call Result",
      }));
  }

  // Legacy string format: "[Role] content" lines
  if (!text || typeof text !== "string") return [];
  const turns = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const m = trimmed.match(/^\[([^\]]+)\]\s*(.*)/);
    if (m) {
      turns.push({
        label: normalizeRole(m[1].trim()),
        text: m[2].trim(),
        start: null,
        end: null,
      });
    } else if (turns.length > 0) {
      turns[turns.length - 1].text += " " + trimmed;
    }
  }
  return turns.filter((t) => !isSystemRole(t.label));
};

export const TranscriptPanel = ({
  transcript,       // generated_transcript object (for observability) OR transcript array (for runs)
  callTranscript,   // raw call_transcript string/array (for observability)
  transcriptionStatus, // "processing" | "completed" | "failed" | null
  currentTime,
  onSeek,
  aiChannel = 0,    // which speaker index is the AI (0 or 1); from call.ai_channel
}) => {
  // Show loading state while transcription is in progress
  if (transcriptionStatus === "processing") {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <div className="h-4 w-4 border-2 border-border border-t-gray-500 rounded-full animate-spin" />
        <span className="text-sm">Generating transcript…</span>
      </div>
    );
  }
  // Normalize speaker label: handles AssemblyAI ("A"/"B") and Deepgram ("0"/"1") formats
  const speakerLabel = (speaker) => {
    const isAgent =
      speaker === "A" ||
      speaker === 0 ||
      speaker === "0" ||
      String(speaker) === String(aiChannel);
    return isAgent ? "Real Agent" : "User";
  };
  // Handle run-style transcript (simple array of { speaker, text, start_second, ... })
  if (Array.isArray(transcript) && transcript.length > 0 && transcript[0]?.speaker != null) {
    return (
      <div className="divide-y divide-gray-100">
        {transcript.map((entry, idx) => {
          const isAgent = entry.speaker === "real_agent";
          const isFnCall = entry.speaker === "function_call";
          const isFnResult = entry.speaker === "function_call_result";
          const isFn = isFnCall || isFnResult;
          const startSec =
            entry.start_second ??
            (entry.start_time ? Date.parse(entry.start_time) / 1000 : null);
          const active =
            startSec != null &&
            currentTime > 0 &&
            Math.abs(currentTime - startSec) < 2;
          return (
            <div
              key={idx}
              className={`px-4 py-3 transition-colors cursor-pointer hover:bg-muted/50 ${active ? "bg-blue-50" : ""} ${isFn ? "bg-amber-50/50" : ""}`}
              onClick={() => startSec != null && onSeek && onSeek(startSec)}
            >
              <div className="flex items-baseline justify-between mb-0.5">
                <span
                  className={`text-xs font-semibold ${isFnCall ? "text-amber-600" : isFnResult ? "text-amber-500" : isAgent ? "text-foreground" : "text-indigo-600"}`}
                >
                  {isFnCall ? "Function Call" : isFnResult ? "Function Result" : isAgent ? "Real Agent" : "Test Agent"}
                </span>
                {startSec != null && (
                  <span className="text-xs text-muted-foreground font-mono">
                    {formatTimestamp(startSec * 1000)}
                  </span>
                )}
              </div>
              <p className={`text-sm leading-relaxed ${isFn ? "font-mono text-amber-700 text-xs" : "text-foreground"}`}>
                {entry.text}
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  // Observability-style transcript (generated_transcript object + callTranscript)
  if (!transcript && !callTranscript)
    return (
      <p className="text-sm text-muted-foreground p-4">No transcript available.</p>
    );

  const isProvided = transcript?.source === "provided";
  let segments = [];
  let showTimestamp = false;

  // Prefer callTranscript when it has proper role labels (e.g. "AI Assistant", "User").
  // Generated transcripts from Deepgram/AssemblyAI sometimes fail diarization and
  // assign all utterances to a single speaker — callTranscript is more reliable.
  const callTranscriptHasRoles = (() => {
    if (!callTranscript) return false;
    const parsed = parseRoleTranscript(callTranscript);
    if (parsed.length < 2) return false;
    const uniqueLabels = new Set(parsed.map((s) => s.label));
    return uniqueLabels.size >= 2;
  })();

  if (callTranscriptHasRoles) {
    segments = parseRoleTranscript(callTranscript);
    showTimestamp = segments.some((s) => s.start != null);
  } else if (isProvided && callTranscript) {
    segments = parseRoleTranscript(callTranscript);
    showTimestamp = segments.some((s) => s.start != null);
  } else if (transcript?.utterances?.length > 0) {
    segments = transcript.utterances.map((u) => ({
      label: speakerLabel(u.speaker),
      text: u.text,
      start: u.start,
      end: u.end,
    }));
    showTimestamp = false;
  } else if (transcript?.words?.length > 0) {
    const words = transcript.words;
    const groups = [];
    let current = null;
    words.forEach((w, i) => {
      const next = words[i + 1];
      if (!current) current = { text: [w.text], start: w.start };
      else current.text.push(w.text);
      if (!next || next.start - w.end > 1500) {
        groups.push({
          label: groups.length % 2 === 0 ? "Main Agent" : "User",
          text: current.text.join(" "),
          start: current.start,
          end: w.end,
        });
        current = null;
      }
    });
    segments = groups;
    showTimestamp = false;
  } else if (callTranscript) {
    segments = parseRoleTranscript(callTranscript);
    showTimestamp = segments.some((s) => s.start != null);
  }

  if (segments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground p-4">No transcript available.</p>
    );
  }

  const isActive = (seg) =>
    seg.start != null &&
    currentTime > 0 &&
    currentTime >= seg.start / 1000 - 0.1 &&
    currentTime <= seg.end / 1000 + 0.1;

  return (
    <div className="divide-y divide-gray-100">
      {segments.map((seg, idx) => {
        const active = isActive(seg);
        const isAgent = seg.label === "AI Assistant" || seg.label === "Main Agent";
        const isFn = seg.isFunctionCall;

        let fnArgs = null;
        let fnResult = null;
        if (isFn && seg.fnData) {
          if (seg.fnData.arguments) {
            try {
              fnArgs = typeof seg.fnData.arguments === "string"
                ? JSON.parse(seg.fnData.arguments)
                : seg.fnData.arguments;
            } catch {
              fnArgs = { raw: seg.fnData.arguments };
            }
          }
          if (seg.fnData.result != null) {
            try {
              fnResult = typeof seg.fnData.result === "string"
                ? (seg.fnData.result.startsWith("{") || seg.fnData.result.startsWith("[")
                    ? JSON.parse(seg.fnData.result)
                    : seg.fnData.result)
                : seg.fnData.result;
            } catch {
              fnResult = seg.fnData.result;
            }
          }
        }

        return (
          <div
            key={idx}
            className={`px-4 py-3 transition-colors ${isFn ? "bg-amber-50/50 border-l-2 border-amber-200" : "cursor-pointer hover:bg-muted/50"} ${active ? "bg-blue-50" : ""}`}
            onClick={() =>
              !isFn && seg.start != null && onSeek && onSeek(seg.start / 1000)
            }
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                {isFn && (
                  <svg className="h-3.5 w-3.5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                <span
                  className={`text-xs font-semibold ${
                    isFn
                      ? "text-amber-700"
                      : isAgent
                        ? "text-foreground"
                        : "text-indigo-600"
                  }`}
                >
                  {isFn && seg.fnData?.name ? seg.fnData.name : seg.label}
                </span>
                {isFn && (
                  <span className="text-xs text-amber-500 font-medium bg-amber-100 px-1.5 py-0.5 rounded">
                    {seg.isFnResult ? "result" : "call"}
                  </span>
                )}
              </div>
              {showTimestamp && seg.start != null && (
                <span className="text-xs text-muted-foreground font-mono">
                  {formatTimestamp(seg.start)}
                </span>
              )}
            </div>

            {isFn ? (
              <div className="mt-1 space-y-1">
                {fnArgs && Object.keys(fnArgs).length > 0 && (
                  <div className="bg-card border border-amber-100 rounded-lg px-3 py-2">
                    {Object.entries(fnArgs).map(([k, v]) => (
                      <div key={k} className="flex gap-2 text-xs font-mono">
                        <span className="text-muted-foreground shrink-0">{k}:</span>
                        <span className="text-foreground break-all">
                          {typeof v === "object" ? JSON.stringify(v) : String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {fnResult != null && (
                  <div className="bg-card border border-amber-100 rounded-lg px-3 py-2">
                    {typeof fnResult === "object" ? (
                      Object.entries(fnResult).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-xs font-mono">
                          <span className="text-muted-foreground shrink-0">{k}:</span>
                          <span className="text-foreground break-all">
                            {typeof v === "object" ? JSON.stringify(v) : String(v)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="text-xs font-mono text-foreground">{String(fnResult)}</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm leading-relaxed text-foreground">{seg.text}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};
